import {
  ReactElement,
  ReactNode,
  createContext,
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FlipCard } from './flip-card';

export interface FlipCardContextValue {
  setBack: (el: ReactNode, show?: boolean) => void;
  showBack: (show: boolean) => void;
}

const FlipCardContext = createContext<FlipCardContextValue | undefined>(
  undefined
);

const useFlipCardContext = () => {
  return useContext(FlipCardContext) as FlipCardContextValue;
};

interface FlipCardProviderProps {
  children?: ReactNode;
}

const FlipCardProvider = forwardRef<
  FlipCardContextValue,
  FlipCardProviderProps
>((props: FlipCardProviderProps, ref): ReactElement => {
  const [back, setBack] = useState<ReactNode>();
  const [showBack, setShowBack] = useState<boolean>(false);

  const contextValue = useMemo<FlipCardContextValue>(() => {
    return {
      setBack: (el: ReactNode, show = false) => {
        setBack(el);
        setShowBack(show);
      },
      showBack: (show: boolean) => {
        setShowBack(show);
      },
    };
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return contextValue;
    },
    [contextValue]
  );

  return (
    <FlipCardContext.Provider value={contextValue}>
      <FlipCard showBack={showBack} dyanamicHeight={true}>
        {/* Wrap children in fragment since ReactNode could of type array and we want the front to be a single element */}
        <>{props.children}</>
        {showBack ? back : undefined}
      </FlipCard>
    </FlipCardContext.Provider>
  );
});

FlipCardProvider.displayName = 'FlipCardProvider';

export { FlipCardProvider, useFlipCardContext };
